// DS3.1 - No label / no bottom padding version
import React, { useRef, useState } from 'react';
import c from 'classnames';
import isFunction from 'lodash/isFunction';
import InputFieldOutline from '../InputFieldOutline';
import BaseInput, { BaseInputProps } from '../BaseInput';
import s from './InputField.scss';

export interface Props extends Omit<BaseInputProps, 'prefix'> {
  /** Preceeds the inner text input. Consider supplying an icon. */
  prefix?: React.ReactNode;
  /** Proceeds the inner text input. Consider supplying an icon. */
  postfix?: React.ReactNode;
  /** Classname set on the inner <input> tag */
  inputClassname?: string;
  /** Will turn the border of the text input green (`success`) or red (`error`) */
  validationResult?: 'success' | 'error';
}

/**
 * One of our standard `<input />`s.
 *
 * Provides space for a prefix and postfix, and allows indication of success or error via `validationResult`.
 */
const InputField = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      type = 'text',
      prefix,
      postfix,
      validationResult,
      className,
      inputClassname,
      disabled,
      onFocus: onFocusProp,
      onBlur: onBlurProp,
      ...rest
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const onFocus = e => {
      setIsFocused(true);
      onFocusProp && onFocusProp(e);
    };
    const onBlur = e => {
      setIsFocused(false);
      onBlurProp && onBlurProp(e);
    };
    const localInputRef = useRef<HTMLInputElement | null>(null);
    return (
      <InputFieldOutline
        className={c(
          s['root'],
          isFocused && s['focused'],
          disabled && s['disabled'],
          className
        )}
        outlineStyle={disabled ? 'disabled' : validationResult}
        onClick={() => localInputRef.current && localInputRef.current.focus()}
      >
        {prefix && <span className={s['prefix']}>{prefix}</span>}
        <BaseInput
          type={type}
          className={c(s['input'], inputClassname)}
          disabled={disabled}
          aria-invalid={validationResult === 'error'}
          ref={node => {
            if (isFunction(ref)) {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
            localInputRef.current = node;
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          {...rest}
        />
        {postfix && <span className={s['postfix']}>{postfix}</span>}
      </InputFieldOutline>
    );
  }
);
InputField.displayName = 'InputField';

export default InputField;
